import { useInboxItems } from "@/api/inbox";
import { IntegrationEnhanced, useOrgIntegrations } from "@/api/integrations";
import { useSkills } from "@/api/skills";
import { InboxItem, Skill } from "@cohesive/api-client";
import Fuse from "fuse.js";
import { useMemo } from "react";

export type SearchResultItem = {
  id: string;
  name: string;
  description?: string;
  itemType: "skills" | "connections" | "inbox";
  data: Skill | IntegrationEnhanced | InboxItem;
};

export interface GlobalSearchResults {
  skills: SearchResultItem[];
  connections: SearchResultItem[];
  inbox: SearchResultItem[];
  totalCount: number;
}

export function useGlobalSearch(searchQuery: string) {
  const { data: skills = [], isLoading: isLoadingSkills } = useSkills();
  const { data: integrations = [], isLoading: isLoadingIntegrations } =
    useOrgIntegrations();
  const { data: inboxItems = [], isLoading: isLoadingInbox } = useInboxItems();

  const isLoading = isLoadingSkills || isLoadingIntegrations || isLoadingInbox;

  const searchResults = useMemo((): GlobalSearchResults | null => {
    if (!searchQuery.trim()) return null;

    const allItems: SearchResultItem[] = [
      ...skills.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        itemType: "skills" as const,
        data: item,
      })),
      ...integrations.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        itemType: "connections" as const,
        data: item,
      })),
      ...inboxItems.map((item) => ({
        id: item.id,
        name: item.flow_session?.name || "Untitled",
        description: item.flow_message?.content,
        itemType: "inbox" as const,
        data: item,
      })),
    ];

    const fuse = new Fuse(allItems, {
      keys: ["name", "description"],
      threshold: 0.3,
    });

    const results = fuse.search(searchQuery).map((result) => result.item);

    return {
      skills: results.filter((item) => item.itemType === "skills"),
      connections: results.filter((item) => item.itemType === "connections"),
      inbox: results.filter((item) => item.itemType === "inbox"),
      totalCount: results.length,
    };
  }, [searchQuery, skills, integrations, inboxItems]);

  return {
    searchResults,
    isLoading,
  };
}
